/**
 * Companio cart page.
 *
 * Ported from the prototype's cart script, with the sample states replaced by
 * real Cart AJAX API calls:
 * - quantity +/- and removal go through /cart/update.js keyed by line `key`,
 *   so gift box lines (linked via the hidden `_gift_for` property) can be
 *   updated in the SAME request as their parent — the "per pair" sync is
 *   therefore atomic and totals never drift
 * - re-rendering uses the Section Rendering API (`sections` param), so the
 *   markup is always what Liquid would have produced
 * - undo re-adds the removed line via /cart/add.js. Custom lines are excluded:
 *   their photos are file-upload properties which cannot be re-attached over
 *   AJAX, so the confirm modal is the point of no return (its copy says so)
 *
 * All listeners are delegated from the <companio-cart> root, so swapping
 * innerHTML after a re-render needs no re-binding.
 *
 * WINDOW D (2026-08-31) — ONE CHANGE, AND ONLY ONE.
 * decodeEntities() was added and is applied to the restore properties in
 * undoRemove(). Nothing else in this file was touched: the three gift box
 * mechanisms (reconcileGiftBox, the `_gift_for` linkage, and the single-request
 * quantity sync) are byte-for-byte what they were, and the whole chain was
 * re-tested in the browser after the change.
 *
 * WHY IT IS NEEDED. sections/companio-cart.liquid now puts the removed line's
 * real properties into `data-restore`, so undoing a gift card brings back its
 * recipient email and message instead of an empty $50 shell. Those values go
 * through Liquid's `json` filter, which HTML-escapes quotes and apostrophes
 * INSIDE the JSON string — `Don't` arrives as `Don&#39;t`. The attribute always
 * parses (that is the useful half of what `json` does), but the values would
 * reach the order carrying visible entities. This turns them back.
 * Do not "simplify" this away, and do not try to fix it by escaping differently
 * in the Liquid: `json` plus `escape` double-encodes, which is how the entities
 * were first noticed.
 *
 * WINDOW G (2026-08-31) — SECOND CHANGE: UNDO NOW BRINGS THE GIFT BOX BACK.
 * Only undoRemove() was rewritten, plus a small addLine() helper it uses.
 * reconcileGiftBox(), addGift(), mutate(), changeQuantity() and the whole
 * remove/modal/toast path are byte-for-byte what they were.
 *
 * WHY. Removing a ready-made line ALSO removes its linked gift box — correct,
 * they are one thing to the customer. But undo only re-added the parent, so
 * $33 of cart became $25 and the $8 gift box was gone. The customer never
 * asked for that; they asked to undo. Measured in the browser on 2026-08-31.
 *
 * WHY IT NEEDS TWO REQUESTS AND NOT ONE. The gift box line finds its parent
 * through `_gift_for`, which holds the parent's LINE KEY. A restored line gets
 * a NEW key from Shopify, so the old value in the removed box line is dead the
 * moment it is removed. The only way to rebuild the link is to add the parent
 * first, read the key back off the /cart/add.js response, and add the box
 * against it — which is the same shape reconcileGiftBox() uses for the handover
 * from the PDP. Verified on 2026-08-31 that /cart/add.js returns the created
 * line item including `key`, and that a second add carrying that key as
 * `_gift_for` links correctly.
 *
 * The parent add deliberately does NOT ask for `sections` when a box is coming:
 * re-rendering between the two requests would show the customer one frame of
 * their line without the gift box on it. Only the second request re-renders.
 * If the key is missing for any reason we reload rather than leave a loose box
 * line in the cart — an unlinked `_gift_for` line renders as nothing at all and
 * still charges.
 *
 * The gift variant id and quantity ride in the `gift` key of `data-restore`,
 * written by sections/companio-cart.liquid. No `gift` key (gift cards, gift
 * notes, any line with no box) means the old single-request path, unchanged.
 */

/**
 * Undo the HTML escaping Liquid's `json` filter applies inside string values.
 * Uses a detached textarea so the browser's own entity table does the work.
 */
function decodeEntities(value) {
  if (typeof value !== 'string' || value.indexOf('&') === -1) return value;
  const holder = document.createElement('textarea');
  holder.innerHTML = value;
  return holder.value;
}

function decodeProperties(properties) {
  if (!properties || typeof properties !== 'object') return {};
  const out = {};
  Object.keys(properties).forEach((key) => {
    const value = decodeEntities(properties[key]);
    // Shopify keeps empty properties on the line, which would show as blank
    // rows on the order. A property that was empty when removed stays absent.
    if (value !== '' && value !== null && value !== undefined) out[key] = value;
  });
  return out;
}

class CompanioCart extends HTMLElement {
  connectedCallback() {
    this.sectionId = this.dataset.sectionId;
    this.giftVariant = Number(this.dataset.giftVariant || 0);
    this.pendingRemove = null;
    this.restorable = null;
    this.undoTimer = null;
    this.busy = false;

    this.addEventListener('click', (event) => this.onClick(event));

    this.reconcileGiftBox();

    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && this.modalOpen()) this.closeModal();
    });
  }

  /* ---------- event delegation ---------- */

  onClick(event) {
    const target = (matcher) => event.target.closest(matcher);
    let el;

    if ((el = target('[data-qty-plus]'))) return this.changeQuantity(el.dataset.qtyPlus, 1);
    if ((el = target('[data-qty-minus]'))) return this.changeQuantity(el.dataset.qtyMinus, -1);
    if ((el = target('[data-remove-key]'))) return this.requestRemove(el);
    if ((el = target('[data-remove-gift]'))) return this.mutate({ [el.dataset.removeGift]: 0 });
    if ((el = target('[data-add-gift]'))) return this.addGift(el);
    if (target('[data-modal-keep]') || target('[data-modal-close]')) return this.closeModal();
    if (target('[data-modal-confirm]')) return this.confirmRemove();
    if (target('[data-undo-button]')) return this.undoRemove();

    const modal = target('.modal');
    if (modal && event.target === modal) this.closeModal();
  }

  /* ---------- cart mutations ---------- */

  lineElement(key) {
    return this.querySelector(`[data-line-key="${CSS.escape(key)}"]`);
  }

  changeQuantity(key, delta) {
    if (this.busy) return;
    const line = this.lineElement(key);
    if (!line) return;
    const current = Number(line.querySelector('.cart-quantity-control span')?.textContent || 1);
    const next = Math.max(1, current + delta);
    if (next === current) return;

    const updates = { [key]: next };
    // "Per pair": the linked gift box follows the parent quantity.
    if (line.dataset.giftKey) updates[line.dataset.giftKey] = next;
    this.mutate(updates);
  }

  requestRemove(button) {
    if (this.busy) return;
    const key = button.dataset.removeKey;
    const giftKey = button.dataset.giftKey || null;

    if (button.dataset.lineMode === 'custom') {
      this.pendingRemove = { key, giftKey };
      this.openModal();
      return;
    }

    let restore = null;
    try {
      restore = button.dataset.restore ? JSON.parse(button.dataset.restore) : null;
    } catch (error) {
      restore = null;
    }
    this.removeLine(key, giftKey, restore);
  }

  confirmRemove() {
    if (!this.pendingRemove) return this.closeModal();
    const { key, giftKey } = this.pendingRemove;
    this.closeModal();
    // No restore payload: photos cannot be re-attached over AJAX.
    this.removeLine(key, giftKey, null, 'Custom item removed. Photos and options were removed.');
  }

  removeLine(key, giftKey, restore, message) {
    const updates = { [key]: 0 };
    if (giftKey) updates[giftKey] = 0;
    this.mutate(updates).then((ok) => {
      if (!ok) return;
      if (restore) {
        this.restorable = restore;
        this.showUndo(restore.message || 'Item removed.');
      } else {
        this.restorable = null;
        this.showUndo(message || 'Item removed.', { withUndo: false });
      }
    });
  }

  /* ---------- gift box handed over from the PDP ---------- */

  /**
   * The PDP form is a multipart POST (it carries the photo files), so it can
   * only ever create one line item. When a gift box was chosen there, the box
   * rides along as a hidden `_gift_box` property holding its variant id and the
   * form redirects here with `?companio_gift=1`. We turn that into a real line
   * and link it back to its parent with `_gift_for`.
   *
   * The flag is stripped from the URL before the request goes out, so a refresh
   * or a back-navigation can never add a second box.
   */
  reconcileGiftBox() {
    const params = new URLSearchParams(window.location.search);
    if (params.get('companio_gift') !== '1') return;

    params.delete('companio_gift');
    const query = params.toString();
    window.history.replaceState({}, '', window.location.pathname + (query ? `?${query}` : ''));

    this.setBusy(true);
    fetch('/cart.js', { headers: { Accept: 'application/json' } })
      .then((response) => (response.ok ? response.json() : Promise.reject(response)))
      .then((cart) => {
        const linked = new Set(
          cart.items.map((item) => item.properties && item.properties._gift_for).filter(Boolean)
        );
        const parent = cart.items.find(
          (item) => item.properties && item.properties._gift_box && !linked.has(item.key)
        );
        if (!parent) return null;

        return fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({
            id: Number(parent.properties._gift_box),
            quantity: parent.quantity,
            properties: { _gift_for: parent.key },
            sections: this.sectionId,
          }),
        }).then((response) => (response.ok ? response.json() : Promise.reject(response)));
      })
      .then((data) => {
        if (data) this.applySections(data.sections);
      })
      .catch(() => {})
      .finally(() => this.setBusy(false));
  }

  addGift(button) {
    if (this.busy || !this.giftVariant) return;
    this.setBusy(true);
    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({
        id: this.giftVariant,
        quantity: Math.max(1, Number(button.dataset.parentQty || 1)),
        properties: { _gift_for: button.dataset.parentKey },
        sections: this.sectionId,
      }),
    })
      .then((response) => (response.ok ? response.json() : Promise.reject(response)))
      .then((data) => this.applySections(data.sections))
      .catch(() => window.location.reload())
      .finally(() => this.setBusy(false));
  }

  /**
   * One /cart/add.js call. Resolves with the created line item, which carries
   * the new `key` — that key is the only way to rebuild a `_gift_for` link.
   */
  addLine(body) {
    return fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    }).then((response) => (response.ok ? response.json() : Promise.reject(response)));
  }

  undoRemove() {
    if (this.busy || !this.restorable) return;
    const { id, quantity, properties, gift } = this.restorable;
    const withGift = Boolean(gift && gift.id);
    this.restorable = null;
    this.hideUndo();
    this.setBusy(true);

    this.addLine({
      id,
      quantity,
      // Liquid's json filter HTML-escapes inside the string — see the file
      // header. Without this the restored line reads "Don&#39;t open...".
      properties: decodeProperties(properties),
      // Skipped when a gift box follows, so the customer never sees a frame of
      // the line without its box. JSON.stringify drops the key when undefined.
      sections: withGift ? undefined : this.sectionId,
    })
      .then((line) => {
        if (!withGift) return line;
        // The restored parent has a NEW key. `_gift_for` must point at that one,
        // not at the dead key the removed box line carried. No key means we
        // cannot link, and an unlinked box line renders as nothing while still
        // being charged — reload instead.
        if (!line || !line.key) return Promise.reject(new Error('no restored line key'));
        return this.addLine({
          id: gift.id,
          quantity: gift.quantity || quantity,
          properties: { _gift_for: line.key },
          sections: this.sectionId,
        });
      })
      .then((data) => this.applySections(data.sections))
      .catch(() => window.location.reload())
      .finally(() => this.setBusy(false));
  }

  mutate(updates) {
    this.setBusy(true);
    return fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ updates, sections: this.sectionId }),
    })
      .then((response) => (response.ok ? response.json() : Promise.reject(response)))
      .then((data) => {
        this.updateBadges(data.item_count);
        this.applySections(data.sections);
        return true;
      })
      .catch(() => {
        window.location.reload();
        return false;
      })
      .finally(() => this.setBusy(false));
  }

  /* ---------- rendering ---------- */

  applySections(sections) {
    const html = sections && sections[this.sectionId];
    if (!html) return;
    const parsed = new DOMParser().parseFromString(html, 'text/html');
    const next = parsed.querySelector('companio-cart');
    if (next) this.innerHTML = next.innerHTML;

    const count = parsed.querySelector('[data-cart-count]');
    if (count) this.updateBadges(count.textContent);
  }

  updateBadges(count) {
    if (count === undefined || count === null) return;
    document.querySelectorAll('[data-cart-count]').forEach((badge) => {
      badge.textContent = String(count).trim();
    });
  }

  setBusy(state) {
    this.busy = state;
    if (state) {
      this.setAttribute('data-busy', '');
    } else {
      this.removeAttribute('data-busy');
    }
  }

  /* ---------- remove modal ---------- */

  modal() {
    return this.querySelector('[data-remove-modal]');
  }

  modalOpen() {
    return this.modal()?.classList.contains('open');
  }

  openModal() {
    this.modal()?.classList.add('open');
    document.body.classList.add('modal-open');
    this.querySelector('[data-modal-keep]')?.focus();
  }

  closeModal() {
    this.pendingRemove = null;
    this.modal()?.classList.remove('open');
    document.body.classList.remove('modal-open');
  }

  /* ---------- undo toast ---------- */

  showUndo(message, options = {}) {
    const toast = this.querySelector('[data-undo-toast]');
    if (!toast) return;
    toast.querySelector('[data-undo-message]').textContent = message;
    const button = toast.querySelector('[data-undo-button]');
    if (button) button.hidden = options.withUndo === false;
    toast.classList.add('show');
    window.clearTimeout(this.undoTimer);
    this.undoTimer = window.setTimeout(() => this.hideUndo(), 12000);
  }

  hideUndo() {
    this.querySelector('[data-undo-toast]')?.classList.remove('show');
  }
}

if (!customElements.get('companio-cart')) {
  customElements.define('companio-cart', CompanioCart);
}
